import { useCallback, useState } from 'react';
import { Receipt } from 'lucide-react';
import { periodReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  firstOfMonth, today, fmtDate, fmtAmtZero, fmtPct, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import { Pager, Dash, InfoBanner, errMessage, DEFAULT_PAGE_SIZE } from './ReportCentreKit';
import type { WhtRegisterFilter, WhtRegisterReportDto, WhtRegisterRow } from '@/types/reportCentre.types';

const REPORT_KEY = 'WHT_REGISTER';

// ── Export ────────────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Date',      header: 'Date',        width: 12 },
  { key: 'Module',    header: 'Module',      width: 12 },
  { key: 'SourceDoc', header: 'Source Doc',  width: 20 },
  { key: 'Type',      header: 'Type',        width: 16 },
  { key: 'Supplier',  header: 'Supplier',    width: 26 },
  { key: 'TaxCode',   header: 'Tax Code',    width: 12 },
  { key: 'TaxName',   header: 'Tax Name',    width: 22 },
  { key: 'Taxable',   header: 'Taxable',     width: 16, numFmt: '#,##0.00' },
  { key: 'Wht',       header: 'WHT Amount',  width: 16, numFmt: '#,##0.00' },
  { key: 'Rate',      header: 'Rate %',      width: 10 },
  { key: 'Journal',   header: 'Journal No',  width: 18 },
  { key: 'Remarks',   header: 'Remarks',     width: 30 },
];

function buildExportRows(rows: WhtRegisterRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    Date:      r.transactionDate,
    Module:    r.sourceModule,
    SourceDoc: r.sourceDocumentNo,
    Type:      r.transactionType,
    Supplier:  r.supplierName ?? '',
    TaxCode:   r.taxCode,
    TaxName:   r.taxCodeName,
    Taxable:   r.taxableAmount,
    Wht:       r.taxAmount,
    Rate:      r.rate ?? '',
    Journal:   r.glJournalNumber ?? '',
    Remarks:   r.remarks ?? '',
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export const WhtRegisterPage = () => {
  const { hasPermission } = usePermission();

  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [data, setData] = useState<WhtRegisterReportDto | null>(null);
  const [applied, setApplied] = useState<WhtRegisterFilter | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async (filter: WhtRegisterFilter) => {
    setLoading(true);
    setError(null);
    try {
      const res = await periodReportApi.getWhtRegister(filter);
      setData(res.data?.data ?? null);
      setApplied(filter);
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const run = useCallback(() => {
    void fetch({ fromDate, toDate, pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
  }, [fromDate, toDate, fetch]);

  if (!hasPermission(Permissions.TAX_REPORT_VIEW)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const rows = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.totalCount / data.pageSize)) : 1;

  const handleExcel = () => {
    if (!data) return;
    exportToExcel({
      sheetName: 'WHT Register',
      columns: EXCEL_COLUMNS,
      rows: buildExportRows(rows),
      title: 'Withholding Tax Register',
      subtitle: `${data.companyName} · ${data.fromDate} – ${data.toDate}`,
      totalsRow: {
        Date: 'TOTAL (all pages)',
        Taxable: data.totals.totalTaxableAmount,
        Wht: data.totals.totalTaxAmount,
      },
    }, `WhtRegister_${data.fromDate}_${data.toDate}`);
  };

  const handleCsv = () => {
    if (!data) return;
    exportToCsvRows(EXCEL_COLUMNS, buildExportRows(rows), `WhtRegister_${data.fromDate}_${data.toDate}`);
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Receipt size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Withholding Tax Register</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={!!data}
          reportKey={REPORT_KEY}
          rowCount={rows.length}
          filterSummary={`from=${fromDate} to=${toDate}`}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4')}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-40">
              <label className={LABEL_CLS}>From *</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={INPUT_CLS} />
            </div>
            <div className="w-40">
              <label className={LABEL_CLS}>To *</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={INPUT_CLS} />
            </div>
          </div>
          <RunReportButton onClick={run} isLoading={loading} disabled={!fromDate || !toDate} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
      )}

      {!data && !loading && (
        <EmptyState message='Set a date window and click "Run Report" to list withholding-tax transactions.' />
      )}

      {data && (
        <div className="flex flex-col gap-3">
          <InfoBanner>Certificate numbers are not tracked in this release.</InfoBanner>

          <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
            <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                WHT register · {fmtDate(data.fromDate)} – {fmtDate(data.toDate)} · Generated {fmtDate(data.generatedAt)}
              </div>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-sm border-collapse min-w-[1400px]">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    <th className={cn(TH_CLS, 'w-28')}>Date</th>
                    <th className={TH_CLS}>Module</th>
                    <th className={TH_CLS}>Source Doc</th>
                    <th className={TH_CLS}>Type</th>
                    <th className={TH_CLS}>Supplier</th>
                    <th className={TH_CLS}>Tax Code</th>
                    <th className={TH_R_CLS}>Taxable</th>
                    <th className={TH_R_CLS}>WHT Amount</th>
                    <th className={cn(TH_R_CLS, 'w-20')}>Rate %</th>
                    <th className={TH_CLS}>Journal No</th>
                    <th className={TH_CLS}>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                        No withholding-tax transactions in the selected window.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {rows.map(r => (
                        <tr key={r.taxTransactionId} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.transactionDate)}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.sourceModule}</td>
                          <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{r.sourceDocumentNo}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.transactionType}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{r.supplierName ?? <Dash />}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap" title={r.taxCodeName}>
                            <span className="font-mono">{r.taxCode}</span>
                          </td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.taxableAmount)}</td>
                          <td className={cn(AMT_CLS, 'font-medium text-gray-900 dark:text-gray-100')}>{fmtAmtZero(r.taxAmount)}</td>
                          <td className={cn(AMT_CLS, 'text-gray-600 dark:text-gray-400')}>
                            {r.rate === null ? <Dash /> : fmtPct(r.rate)}
                          </td>
                          <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.glJournalNumber ?? <Dash />}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.remarks ?? <Dash />}</td>
                        </tr>
                      ))}
                      {/* Totals row — whole filtered window, not just this page */}
                      <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/60 font-semibold">
                        <td colSpan={6} className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                          Total ({data.totalCount.toLocaleString('en-US')} transactions, all pages)
                        </td>
                        <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>
                          {fmtAmtZero(data.totals.totalTaxableAmount)}
                        </td>
                        <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>
                          {fmtAmtZero(data.totals.totalTaxAmount)}
                        </td>
                        <td colSpan={3} />
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
            <Pager
              pageNumber={data.pageNumber}
              totalPages={totalPages}
              totalCount={data.totalCount}
              shownCount={rows.length}
              loading={loading}
              onPage={p => {
                if (applied) void fetch({ ...applied, pageNumber: p });
              }}
              noun="transactions"
            />
          </div>
        </div>
      )}
    </div>
  );
};
